// @ts-strict-ignore
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { AppComponentProps, AppRouteComponentMatchParams } from '../../../typescriptHelpers';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import {
    SyncOutlined,
    PlusOutlined,
    QrcodeOutlined,
    EditOutlined,
    DisconnectOutlined,
    CheckCircleOutlined,
    DeleteOutlined,
    CheckSquareOutlined,
    CloseOutlined,
    ExperimentOutlined,
} from '@ant-design/icons';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import { DownOutlined } from '@ant-design/icons';

import { RegisterDeviceAlert } from '../../RegisterDeviceAlert';
import Dropdown from 'antd/lib/dropdown';
import Search from 'antd/lib/input/Search';
import Table from 'antd/lib/table';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import {
    selectAppDevices,
    selectAppPlatform,
    selectAppSettings,
    selectDevicesRegisteredInASCCount,
    selectShouldShowRegisterDevicesPrompt,
    selectUserCanPerformAction,
    selectUserDevices,
} from '../../../selectors/selectors';
import { globalMessage } from '../../../utils/globalMessage';
import { AppDevice, ASCRegistrationStatus } from '../../../types';
import { EnableDisableDevicesRequest, tryUpdateDevicesRequest, UpdateDevicesRequest } from '../../../actions/devices';
import { DevicesUpdatable } from '../../../actions/updatable';
import { displayDeviceClass } from '../../../helpers/displayHelpers';
import Modal from 'antd/lib/modal';
import { QRCode } from 'react-qrcode-logo';
import { DeviceDetailsDrawer, DeviceStatusTag, ShortUDIDComponent } from './DeviceDetailsDrawer';
import { SortTableButton } from '../../tableFilters/SortTableButton';
import { ManualAddDeviceModal } from './ManualAddDeviceModal';
import { AddDeviceRequest } from '../../../actions/appSettings';
import Tooltip from 'antd/lib/tooltip';
import { useParams } from 'react-router-dom';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import { createAddTestDeviceRoute } from '@/helpers/routeHelpers';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const renderTitle = (title: string) => {
    return <Text strong>{title}</Text>;
};

export interface DevicesSectionProps extends AppComponentProps {
    getAllDevicesIsLoading: boolean;
}

enum SortType {
    Name = 'name',
    DeviceType = 'deviceType',
    AssociatedUser = 'associatedUser',
    UDID = 'udid',
    Status = 'status',
}

const getAppStoreDeviceIdsFromDevices = (devices: AppDevice[], deviceIds: string[]) => {
    return devices.filter((device) => deviceIds.includes(device.id)).map((device) => device.appStoreId ?? device.id);
};

export const generateRegisterTestDeviceMobileLink = ({
    orgIdParam,
    appIdParam,
}: {
    orgIdParam: string;
    appIdParam: string;
}) => {
    return `${window.location.origin}${createAddTestDeviceRoute(orgIdParam, appIdParam)}`;
};

export const DevicesSection: React.FC<DevicesSectionProps> = ({ appId, orgId, getAllDevicesIsLoading }) => {
    const dispatch = useDispatch();
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();

    const [searchValue, setSearchValue] = React.useState('');
    const [isSelecting, setIsSelecting] = React.useState(false);
    const [selectedDevices, setSelectedDevices] = React.useState<string[]>([]);
    const [qrCodeLink, setQRCodeLink] = React.useState<string | null>(null);
    const [selectedSortType, setSelectedSortType] = React.useState<SortType>(SortType.Name);
    const [shouldShowManualAddDeviceModal, setShouldShowManualAddDeviceModal] = React.useState(false);

    const addDeviceIsLoading = useIsLoading(DevicesUpdatable.AddDevice, appId);
    const addDeviceIsError = useError(DevicesUpdatable.AddDevice, appId);
    const addDeviceIsSuccess = useUpdated(DevicesUpdatable.AddDevice, appId);

    React.useEffect(() => {
        if (addDeviceIsError) {
            globalMessage.error(addDeviceIsError);
        }
    }, [addDeviceIsError]);

    React.useEffect(() => {
        if (addDeviceIsSuccess) {
            globalMessage.success('Device added successfully!');
            setShouldShowManualAddDeviceModal(false);
        }
    }, [addDeviceIsSuccess]);

    const devices = useSelector((state: RootState) => selectAppDevices(state, appId));
    const devicesRegisteredInASCCount = useSelector((state: RootState) =>
        selectDevicesRegisteredInASCCount(state, appId),
    );
    const userCanUpdateDevicesInASC = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateDeviceInASC),
    );

    const userCanAddDeleteAppDevice = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAAddRemoveDeviceFromApp),
    );

    const userDevices = useSelector((state: RootState) => selectUserDevices(state));

    const [highlightedDevice, setHighlightedDevice] = React.useState<AppDevice | null>(null);

    const filteredDevices = React.useMemo(() => {
        if (!devices) {
            return [];
        }

        return devices.filter((device) => {
            return (
                device.displayName.toLowerCase().includes(searchValue.toLowerCase()) ||
                device.udid.includes(searchValue)
            );
        });
    }, [devices, searchValue]);

    const sortedDevices = React.useMemo(() => {
        const sortedDevices = [...(filteredDevices ?? [])];

        switch (selectedSortType) {
            case SortType.Name:
                sortedDevices.sort((a, b) => {
                    return a.displayName.localeCompare(b.displayName);
                });
                break;
            case SortType.DeviceType:
                sortedDevices.sort((a, b) => {
                    return (a.deviceClass ?? '').localeCompare(b.deviceClass ?? '');
                });
                break;
            case SortType.AssociatedUser:
                sortedDevices.sort((a, b) => {
                    return (a.associatedUserEmail ?? '').localeCompare(b.associatedUserEmail ?? '');
                });
                break;
            case SortType.UDID:
                sortedDevices.sort((a, b) => {
                    return a.udid.localeCompare(b.udid);
                });
                break;
            case SortType.Status:
                sortedDevices.sort((a, b) => {
                    return (a.ascRegistrationStatus ?? '').localeCompare(b.ascRegistrationStatus ?? '');
                });
                break;
            default:
                break;
        }

        return sortedDevices;
    }, [filteredDevices, selectedSortType]);

    const isRegisteringDevices = useIsLoading(DevicesUpdatable.RegisterDevices, appId);
    const isDisablingDevices = useIsLoading(DevicesUpdatable.DisableEnableDevices, appId);
    const isDeletingDevices = useIsLoading(DevicesUpdatable.DeleteDevices, appId);

    const registeringDevicesError = useError(DevicesUpdatable.RegisterDevices, appId);
    const disablingEnablingDevicesError = useError(DevicesUpdatable.DisableEnableDevices, appId);
    const deletingDevicesError = useError(DevicesUpdatable.DeleteDevices, appId);

    const registerDeviceSuccess = useUpdated(DevicesUpdatable.RegisterDevices, appId);
    const disableEnableDevicesSuccess = useUpdated(DevicesUpdatable.DisableEnableDevices, appId);
    const deleteDeviceSuccess = useUpdated(DevicesUpdatable.DeleteDevices, appId);

    React.useEffect(() => {
        if (registerDeviceSuccess) {
            globalMessage.success(
                'The selected devices have been successfully registered in the Apple Developer Portal',
            );
        }
    }, [registerDeviceSuccess]);

    React.useEffect(() => {
        if (disableEnableDevicesSuccess) {
            globalMessage.success('The selected devices have been successfully updated in the Apple Developer Portal');
        }
    }, [disableEnableDevicesSuccess]);

    React.useEffect(() => {
        if (deleteDeviceSuccess) {
            globalMessage.success('The selected devices have been successfully removed from Runway');
        }
    }, [deleteDeviceSuccess]);

    React.useEffect(() => {
        if (registeringDevicesError) {
            globalMessage.error(registeringDevicesError);
        }
    }, [registeringDevicesError]);

    React.useEffect(() => {
        if (disablingEnablingDevicesError) {
            globalMessage.error(disablingEnablingDevicesError);
        }
    }, [disablingEnablingDevicesError]);

    React.useEffect(() => {
        if (deletingDevicesError) {
            globalMessage.error(deletingDevicesError);
        }
    }, [deletingDevicesError]);

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const shouldHideRegisterDeviceAlert = useSelector((state: RootState) => {
        if (!devices) {
            return true;
        }

        const hideRegisterDevicesPrompt = selectAppSettings(state, appId).shouldHideRegisterDevicePrompt;
        return (
            devices.some((device) => userDevices.some((userDevice) => userDevice.udid === device.udid)) ||
            !selectShouldShowRegisterDevicesPrompt(state, devices, appPlatform, orgId) ||
            hideRegisterDevicesPrompt
        );
    });

    return (
        <div
            style={{
                width: '100%',
                marginTop: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 32,
                alignItems: 'stretch',
            }}
        >
            {!shouldHideRegisterDeviceAlert && (
                <RegisterDeviceAlert
                    style={{
                        marginBottom: 0,
                    }}
                />
            )}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Space
                    split={
                        <Divider
                            style={{
                                margin: 0,
                            }}
                            type="vertical"
                        />
                    }
                >
                    <Text
                        style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: antGrey.grey8,
                        }}
                    >
                        Devices ({devices?.length ?? 0})
                    </Text>
                    <Text
                        style={{
                            fontSize: 14,
                            color: antGrey.grey7,
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 600,
                                color: antGrey.grey8,
                            }}
                        >
                            {devicesRegisteredInASCCount}
                        </span>{' '}
                        registered with Apple Developer Portal
                    </Text>
                </Space>
                <Dropdown
                    menu={{
                        items: [
                            {
                                label: 'Register on device from QR code',
                                key: '1',
                                icon: (
                                    <QrcodeOutlined
                                        style={{
                                            color: runwayColors.primary,
                                        }}
                                    />
                                ),
                                onClick: () => {
                                    setQRCodeLink(
                                        generateRegisterTestDeviceMobileLink({
                                            orgIdParam,
                                            appIdParam,
                                        }),
                                    );
                                },
                            },
                            {
                                label: 'Register manually',
                                key: '2',
                                icon: (
                                    <EditOutlined
                                        style={{
                                            color: runwayColors.primary,
                                        }}
                                    />
                                ),
                                onClick: () => {
                                    setShouldShowManualAddDeviceModal(true);
                                },
                            },
                        ],
                    }}
                >
                    <Button type="primary" icon={<PlusOutlined />}>
                        <Space style={{ marginLeft: 4 }}>
                            Add a test device
                            <DownOutlined />
                        </Space>
                    </Button>
                </Dropdown>
            </div>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Space>
                    <Search
                        placeholder="Search"
                        allowClear
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onSearch={(value) => {
                            setSearchValue(value);
                        }}
                        style={{ width: 320 }}
                    />
                    <SortTableButton<SortType>
                        selectedSortType={selectedSortType}
                        sortOptions={[
                            {
                                sortType: SortType.Name,
                                text: 'Name',
                            },
                            {
                                sortType: SortType.DeviceType,
                                text: 'Device type',
                            },
                            {
                                sortType: SortType.AssociatedUser,
                                text: 'Associated user',
                            },
                            {
                                sortType: SortType.UDID,
                                text: 'UDID',
                            },
                            {
                                sortType: SortType.Status,
                                text: 'Status',
                            },
                        ]}
                        onSelectSort={(sortType) => {
                            setSelectedSortType(sortType);
                        }}
                    />
                </Space>
                <Space>
                    {isSelecting && (
                        <Dropdown
                            disabled={selectedDevices.length === 0}
                            menu={{
                                items: [
                                    {
                                        label: 'Register with Apple Developer Portal',
                                        key: '1',
                                        icon: (
                                            <SyncOutlined
                                                style={{
                                                    color: runwayColors.primary,
                                                }}
                                            />
                                        ),
                                        onClick: () => {
                                            // validate that there are devices that are not registered in ASC
                                            const someDevicesNotRegisteredInASC = devices
                                                .filter((device) => selectedDevices.includes(device.id))
                                                .some((device) => {
                                                    return !device.ascRegistrationStatus;
                                                });

                                            if (!someDevicesNotRegisteredInASC) {
                                                globalMessage.error(
                                                    'All selected devices are already registered in the Apple Developer Portal',
                                                );
                                                return;
                                            }

                                            if (!userCanUpdateDevicesInASC) {
                                                globalMessage.error(DISALLOWED_ACTION_ERROR_MESSAGE);
                                                return;
                                            }

                                            const request: UpdateDevicesRequest = {
                                                deviceIds: selectedDevices,
                                                appId,
                                                orgId,
                                            };
                                            dispatch(
                                                tryUpdateDevicesRequest(DevicesUpdatable.RegisterDevices, request),
                                            );
                                        },
                                    },
                                    {
                                        label: 'Disable from Apple Developer Portal',
                                        key: '2',
                                        icon: (
                                            <DisconnectOutlined
                                                style={{
                                                    color: runwayColors.primary,
                                                }}
                                            />
                                        ),
                                        onClick: () => {
                                            // validate that there are devices registered in ASC
                                            const someDevicesRegisteredInASC = devices
                                                .filter((device) => selectedDevices.includes(device.id))
                                                .some((device) => {
                                                    return device.ascRegistrationStatus;
                                                });

                                            if (!someDevicesRegisteredInASC) {
                                                globalMessage.error(
                                                    'None of the selected devices are registered in the Apple Developer Portal',
                                                );
                                                return;
                                            }

                                            if (!userCanUpdateDevicesInASC) {
                                                globalMessage.error(DISALLOWED_ACTION_ERROR_MESSAGE);
                                                return;
                                            }

                                            const request: EnableDisableDevicesRequest = {
                                                deviceIds: getAppStoreDeviceIdsFromDevices(devices, selectedDevices),
                                                appId,
                                                orgId,
                                                enable: false,
                                            };
                                            dispatch(
                                                tryUpdateDevicesRequest(DevicesUpdatable.DisableEnableDevices, request),
                                            );
                                        },
                                    },
                                    {
                                        label: 'Enable in Apple Developer Portal',
                                        key: '3',
                                        icon: (
                                            <CheckCircleOutlined
                                                style={{
                                                    color: runwayColors.primary,
                                                }}
                                            />
                                        ),
                                        onClick: () => {
                                            // validate that there are devices registered in ASC
                                            const someDevicesRegisteredInASC = devices
                                                .filter((device) => selectedDevices.includes(device.id))
                                                .some((device) => {
                                                    return device.ascRegistrationStatus;
                                                });

                                            if (!someDevicesRegisteredInASC) {
                                                globalMessage.error(
                                                    'None of the selected devices are registered in the Apple Developer Portal',
                                                );
                                                return;
                                            }

                                            if (!userCanUpdateDevicesInASC) {
                                                globalMessage.error(DISALLOWED_ACTION_ERROR_MESSAGE);
                                                return;
                                            }

                                            const request: EnableDisableDevicesRequest = {
                                                deviceIds: getAppStoreDeviceIdsFromDevices(devices, selectedDevices),
                                                appId,
                                                orgId,
                                                enable: true,
                                            };
                                            dispatch(
                                                tryUpdateDevicesRequest(DevicesUpdatable.DisableEnableDevices, request),
                                            );
                                        },
                                    },
                                    {
                                        label: 'Delete from Runway',
                                        key: '4',
                                        icon: (
                                            <DeleteOutlined
                                                style={{
                                                    color: runwayColors.primary,
                                                }}
                                            />
                                        ),
                                        onClick: () => {
                                            // validate that there are devices registered in Runway
                                            const someDevicesRegisteredInRunway = devices
                                                .filter((device) => selectedDevices.includes(device.id))
                                                .some((device) => {
                                                    return device.isRegisteredInRunway;
                                                });

                                            if (!someDevicesRegisteredInRunway) {
                                                globalMessage.error(
                                                    'None of the selected devices can be deleted from Runway',
                                                );
                                                return;
                                            }

                                            if (!userCanAddDeleteAppDevice) {
                                                globalMessage.error(DISALLOWED_ACTION_ERROR_MESSAGE);
                                                return;
                                            }

                                            const request: UpdateDevicesRequest = {
                                                deviceIds: selectedDevices,
                                                appId,
                                                orgId,
                                            };
                                            dispatch(tryUpdateDevicesRequest(DevicesUpdatable.DeleteDevices, request));
                                        },
                                    },
                                ],
                            }}
                        >
                            <Button
                                loading={isRegisteringDevices || isDisablingDevices || isDeletingDevices}
                                icon={
                                    <CheckSquareOutlined
                                        style={{
                                            color: runwayColors.primary,
                                        }}
                                    />
                                }
                            >
                                <Space
                                    style={{
                                        marginLeft: 12,
                                    }}
                                >
                                    <Text>Actions</Text>
                                    <DownOutlined />
                                </Space>
                            </Button>
                        </Dropdown>
                    )}
                    <Button
                        icon={
                            isSelecting ?
                                <CloseOutlined
                                    style={{
                                        color: runwayColors.primary,
                                    }}
                                />
                            :   <CheckSquareOutlined
                                    style={{
                                        color: runwayColors.primary,
                                    }}
                                />
                        }
                        onClick={() => setIsSelecting(!isSelecting)}
                    >
                        {isSelecting ? 'Cancel select' : 'Select'}
                    </Button>
                </Space>
            </div>
            <Table
                loading={getAllDevicesIsLoading || isRegisteringDevices || isDisablingDevices || isDeletingDevices}
                rowSelection={
                    isSelecting ?
                        {
                            type: 'checkbox',
                            onChange: (selectedRowKeys: React.Key[]) => {
                                setSelectedDevices(selectedRowKeys as string[]);
                            },
                            selectedRowKeys: selectedDevices,
                        }
                    :   null
                }
                dataSource={
                    sortedDevices?.map((device) => ({
                        ...device,
                        key: device.id,
                    })) ?? []
                }
                locale={{
                    emptyText: 'No devices yet.',
                }}
                onRow={(device) => {
                    return {
                        onClick: () => {
                            setHighlightedDevice(device);
                        },
                    };
                }}
                rowClassName={(device) => {
                    return (
                        (device.ascRegistrationStatus == ASCRegistrationStatus.Disabled ||
                            device.ascRegistrationStatus == ASCRegistrationStatus.Ineligible) &&
                        'disabled-device'
                    );
                }}
                columns={[
                    {
                        title: renderTitle('Name'),
                        key: 'displayName',
                        render: (device: AppDevice) => {
                            return (
                                <Space>
                                    <Text>{device.displayName}</Text>
                                    {device.isTestDevice && (
                                        <Tooltip title="This is a test device. Test devices are not associated with any users in Runway.">
                                            <ExperimentOutlined style={{ color: antGrey.grey6 }} />
                                        </Tooltip>
                                    )}
                                </Space>
                            );
                        },
                    },
                    {
                        title: renderTitle('Device type'),
                        key: 'deviceClass',
                        render: (device: AppDevice) => {
                            return device?.deviceClass ?
                                    displayDeviceClass(device?.deviceClass)
                                :   <Text type="secondary">Unknown</Text>;
                        },
                    },
                    {
                        title: renderTitle('Associated user'),
                        key: 'associatedUserEmail',
                        render: (device: AppDevice) => {
                            const associatedUserEmail = device.associatedUserEmail;

                            if (!associatedUserEmail) {
                                return <Text type="secondary">(none)</Text>;
                            }

                            return device.associatedUserEmail;
                        },
                    },
                    {
                        title: renderTitle('UDID'),
                        key: 'udid',
                        render: (device: AppDevice) => {
                            return <ShortUDIDComponent udid={device.udid} maxWidth={75} />;
                        },
                    },
                    {
                        title: renderTitle('Status'),
                        key: 'status',
                        render: (device: AppDevice) => {
                            return <DeviceStatusTag device={device} shouldShowAutomationStatusInsideTooltip />;
                        },
                    },
                ]}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
            />
            <DeviceDetailsDrawer
                appId={appId}
                orgId={orgId}
                device={highlightedDevice}
                onClose={() => setHighlightedDevice(null)}
                shouldShowRegistrationStatus={true}
            />
            <ManualAddDeviceModal
                title="Add a test device"
                isVisible={shouldShowManualAddDeviceModal}
                isLoading={addDeviceIsLoading}
                modalContent={
                    <Text>
                        Add a test device for this app. Test devices are not associated with any users in Runway.
                    </Text>
                }
                onSubmit={(values) => {
                    if (!userCanAddDeleteAppDevice) {
                        globalMessage.error(DISALLOWED_ACTION_ERROR_MESSAGE);
                        return;
                    }
                    const request: AddDeviceRequest = {
                        appId,
                        orgId,
                        ...values,
                    };
                    dispatch(tryUpdateDevicesRequest(DevicesUpdatable.AddDevice, request));
                }}
                onCancel={() => {
                    setShouldShowManualAddDeviceModal(false);
                }}
            />
            <Modal open={!!qrCodeLink} onCancel={() => setQRCodeLink(null)} destroyOnClose footer={null}>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <QRCode value={qrCodeLink} size={420} />
                </div>
            </Modal>
        </div>
    );
};
